import { useState } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { toast } from 'sonner';
import { isAxiosError } from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/date';
import type { EmployeeListItem } from '@/types/employee';
import {
  usePreboardingInvitations,
  useResendPreboardingInvitation,
  useSendPreboardingInvitation,
  type PreboardingInvitation,
  type PreboardingInvitationStatus,
} from '@/hooks/use-preboarding-invitations';
import { Loader2, Mail, RotateCcw, Send } from 'lucide-react';
import { RecipientCombobox } from './recipient-combobox';

const STATUS_BADGE: Record<PreboardingInvitationStatus, string> = {
  PENDING: 'text-amber-600 border-amber-600/30 bg-amber-600/5',
  COMPLETED: 'text-green-600 border-green-600/30 bg-green-600/5',
  EXPIRED: 'text-muted-foreground border-border bg-muted/50',
  REVOKED: 'text-muted-foreground border-border bg-muted/50',
};

function errorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string | string[] } | undefined;
    const message = data?.message;
    if (Array.isArray(message)) return message[0] ?? fallback;
    if (typeof message === 'string') return message;
  }
  return fallback;
}

interface SendInvitationDialogProps {
  formId: string;
  /** Disabled (with a hint) until the form is PUBLISHED — the backend rejects sends otherwise. */
  isPublished: boolean;
}

export function SendInvitationDialog({ formId, isPublished }: SendInvitationDialogProps) {
  const formatName = useEmployeeNameFormatter();
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState<EmployeeListItem | null>(null);

  const { data: invitations = [], isLoading } = usePreboardingInvitations(formId, open);
  const sendMutation = useSendPreboardingInvitation(formId);
  const resendMutation = useResendPreboardingInvitation(formId);

  const handleSend = () => {
    if (!recipient) return;
    sendMutation.mutate(recipient.id, {
      onSuccess: () => {
        toast.success(`Pre-boarding form sent to ${formatName({ firstName: recipient.firstName, lastName: recipient.lastName })}`);
        setRecipient(null);
      },
      onError: (error) => toast.error(errorMessage(error, 'Could not send the form')),
    });
  };

  const handleResend = (invitation: PreboardingInvitation) => {
    resendMutation.mutate(invitation.employeeId, {
      onSuccess: () => toast.success(`A fresh link was sent to ${invitation.recipientEmail}`),
      onError: (error) => toast.error(errorMessage(error, 'Could not resend the link')),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="shrink-0" disabled={!isPublished}>
          <Send className="mr-2 h-4 w-4" />
          Send to employee
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send pre-boarding form</DialogTitle>
          <DialogDescription>
            Issue a single-use secure link (valid for 3 days) and email it to the employee.
          </DialogDescription>
        </DialogHeader>

        {/* Send a new invitation */}
        <div className="flex items-end gap-2">
          <div className="flex-1 min-w-0">
            <RecipientCombobox
              value={recipient}
              onChange={setRecipient}
              disabled={sendMutation.isPending}
            />
          </div>
          <Button onClick={handleSend} disabled={!recipient || sendMutation.isPending}>
            {sendMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Send
          </Button>
        </div>

        {/* Already sent */}
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Sent invitations
          </p>
          {isLoading ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : invitations.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No invitations sent yet.
            </p>
          ) : (
            <ul className="max-h-64 space-y-1.5 overflow-y-auto">
              {invitations.map((invitation) => {
                const canResend =
                  invitation.status !== 'COMPLETED' && !resendMutation.isPending;
                return (
                  <li
                    key={invitation.id}
                    className="flex items-center gap-3 rounded-md border border-border p-2.5"
                  >
                    <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{invitation.recipientEmail}</p>
                      <p className="text-xs text-muted-foreground">
                        Sent {formatDateTime(invitation.sentAt, { dateStyle: 'medium' })}
                        {invitation.status === 'PENDING' &&
                          ` · expires ${formatDateTime(invitation.expiresAt, { dateStyle: 'medium' })}`}
                        {invitation.status === 'COMPLETED' &&
                          invitation.completedAt &&
                          ` · completed ${formatDateTime(invitation.completedAt, { dateStyle: 'medium' })}`}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className={cn('text-[11px]', STATUS_BADGE[invitation.status])}
                    >
                      {invitation.status}
                    </Badge>
                    {invitation.status !== 'COMPLETED' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => handleResend(invitation)}
                        disabled={!canResend}
                        title="Revoke the old link and send a fresh one"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        <span className="sr-only">Resend</span>
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
